import React, { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '../../supabaseClient';
import Dropdown from '../Dropdown';
import SearchableClientDropdown from '../SearchableClientDropdown';
import SearchableContactDropdown from '../SearchableContactDropdown';
import { DropdownItem } from '../Dropdown/config';
import AddressAutocomplete, { AddressFields } from '../AddressAutocomplete';
import Modal from '../Modal';
import PanelHeading from '../PanelHeading';
import Field from '../Field';
import TextInput from '../TextInput';
import SegmentedControl from '../SegmentedControl';
import SaveDot, { SaveState } from '../SaveDot';
import IconButton from '../IconButton';
import { ChevronDownIcon } from '../IconButton/icons';
import MemberChips from '../MemberChips';
import { PROJECT_JOB_TYPE_OPTIONS, JOB_TYPE_HINTS, ProjectJobType } from '../ProjectDetailModal/config';

interface TeamMember {
  id: number | string;
  first_name: string;
  last_name: string;
}

interface AddJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  onJobCreated: () => void;
  teamMembers: TeamMember[];
  defaultType?: string; // Optional preset type - hides dropdown when set
}

interface FormData {
  job_number: string;
  description: string;
  types: string;
  project_lead: string;
  client: string;
  primary_contact: string;
  team_members: string[];
  street_address: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
}

interface AddressGeoData {
  latitude: number | null;
  longitude: number | null;
  mapbox_data: Record<string, string> | null;
}

interface FormErrors {
  job_number?: string;
  description?: string;
  types?: string;
  newClient?: string;
  newContact?: string;
  general?: string;
}

const AddJobModal: React.FC<AddJobModalProps> = ({
  isOpen,
  onClose,
  onJobCreated,
  teamMembers,
  defaultType
}) => {
  const [formData, setFormData] = useState<FormData>({
    job_number: '',
    description: '',
    types: defaultType || '',
    project_lead: '',
    client: '',
    primary_contact: '',
    team_members: [],
    street_address: '',
    city: '',
    state: '',
    postcode: '',
    country: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddingClient, setIsAddingClient] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [isCreatingClient, setIsCreatingClient] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [isAddingContact, setIsAddingContact] = useState(false);
  const [newContactFirstName, setNewContactFirstName] = useState('');
  const [newContactLastName, setNewContactLastName] = useState('');
  const [isCreatingContact, setIsCreatingContact] = useState(false);
  const [contactRefreshKey, setContactRefreshKey] = useState(0);
  
  // Job number validation state
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [defaultJobNumber, setDefaultJobNumber] = useState<string>('');
  const duplicateCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Address state
  const [siteAddressDisplay, setSiteAddressDisplay] = useState('');
  const [showAddressDetails, setShowAddressDetails] = useState(false);
  const [addressGeoData, setAddressGeoData] = useState<AddressGeoData>({
    latitude: null,
    longitude: null,
    mapbox_data: null,
  });

  // Fetch the next available job number (from projects table only, matching the DB trigger)
  const fetchNextJobNumber = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('job_number')
        .order('job_number', { ascending: false })
        .limit(1);

      if (error) throw error;
      
      const maxNum = data?.[0]?.job_number || 0;
      return `${maxNum + 1}`;
    } catch (err) {
      console.error('Error fetching next job number:', err);
      return '1'; // Fallback
    }
  }, []);

  // Check if job number already exists in projects table (debounced)
  const checkDuplicateJobNumber = useCallback(async (jobNumber: string) => {
    if (!jobNumber.trim()) {
      setIsDuplicate(false);
      return;
    }

    const numericJobNumber = parseInt(jobNumber.trim(), 10);
    if (isNaN(numericJobNumber)) {
      setIsDuplicate(false);
      setErrors(prev => ({ ...prev, job_number: 'Job number must be a number' }));
      return;
    }

    setIsCheckingDuplicate(true);
    try {
      // Only check projects table - projects and stages have separate job number sequences
      const { data, error } = await supabase
        .from('projects')
        .select('id')
        .eq('job_number', numericJobNumber)
        .limit(1);

      if (error) throw error;

      const exists = data && data.length > 0;
      setIsDuplicate(exists);
      if (exists) {
        setErrors(prev => ({ ...prev, job_number: 'Job number already exists' }));
      } else {
        setErrors(prev => ({ ...prev, job_number: undefined }));
      }
    } catch (err) {
      console.error('Error checking duplicate:', err);
    } finally {
      setIsCheckingDuplicate(false);
    }
  }, []);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        job_number: '',
        description: '',
        types: defaultType || '',
        project_lead: '',
        client: '',
        primary_contact: '',
        team_members: [],
        street_address: '',
        city: '',
        state: '',
        postcode: '',
        country: '',
      });
      setErrors({});
      setIsAddingClient(false);
      setNewClientName('');
      setIsAddingContact(false);
      setNewContactFirstName('');
      setNewContactLastName('');
      setIsDuplicate(false);
      setIsCheckingDuplicate(false);
      setSiteAddressDisplay('');
      setShowAddressDetails(false);
      setAddressGeoData({ latitude: null, longitude: null, mapbox_data: null });

      // Fetch next job number
      fetchNextJobNumber().then(nextNumber => {
        setFormData(prev => ({ ...prev, job_number: nextNumber }));
        setDefaultJobNumber(nextNumber);
      });
    }

    // Cleanup timeout on close
    return () => {
      if (duplicateCheckTimeoutRef.current) {
        clearTimeout(duplicateCheckTimeoutRef.current);
      }
    };
  }, [isOpen, defaultType, fetchNextJobNumber]);

  // Sync project lead with team members - when project lead is selected, add them to team members
  React.useEffect(() => {
    if (formData.project_lead) {
      setFormData(prev => {
        const alreadyInTeam = prev.team_members.includes(prev.project_lead);
        if (!alreadyInTeam) {
          return {
            ...prev,
            team_members: [...prev.team_members, prev.project_lead]
          };
        }
        return prev;
      });
    }
  }, [formData.project_lead]);

  const validateForm = useCallback((): FormErrors => {
    const newErrors: FormErrors = {};

    if (!formData.job_number.trim()) {
      newErrors.job_number = 'Job number is required';
    } else if (isNaN(parseInt(formData.job_number.trim(), 10))) {
      newErrors.job_number = 'Job number must be a number';
    } else if (isDuplicate) {
      newErrors.job_number = 'Job number already exists';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Project description is required';
    }

    // Only validate types if no defaultType is provided
    if (!defaultType && !formData.types) {
      newErrors.types = 'Type is required';
    }

    return newErrors;
  }, [formData, defaultType, isDuplicate]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Use defaultType if provided, otherwise use form value
      const projectType = defaultType || formData.types;

      // Call RPC to create project with team members in one transaction
      const { data, error } = await supabase.rpc('create_project_with_team', {
        p_description: formData.description.trim(),
        p_types: projectType,
        p_project_lead: formData.project_lead || null,
        p_client: formData.client ? parseInt(formData.client) : null,
        p_team_member_ids: formData.team_members.length > 0 ? formData.team_members : null,
        p_job_number: formData.job_number ? parseInt(formData.job_number.trim(), 10) : null
      });

      if (error) {
        throw error;
      }

      if (data && !data.success) {
        throw new Error(data.error || 'Failed to create project');
      }

      if (data?.project_id) {
        const extraData: Record<string, unknown> = {};

        if (formData.primary_contact) {
          extraData.primary_contact = formData.primary_contact;
        }

        if (formData.street_address || addressGeoData.latitude != null) {
          extraData.street_address = formData.street_address || null;
          extraData.city = formData.city || null;
          extraData.state = formData.state || null;
          extraData.postcode = formData.postcode || null;
          extraData.country = formData.country || null;
          extraData.latitude = addressGeoData.latitude;
          extraData.longitude = addressGeoData.longitude;
          extraData.mapbox_data = addressGeoData.mapbox_data;
        }

        if (Object.keys(extraData).length > 0) {
          await supabase.rpc('update_project_detail', {
            project_id: data.project_id,
            project_data: extraData
          });
        }
      }

      onJobCreated();
      onClose();
    } catch (err) {
      console.error('Error creating project:', err);
      setErrors({
        general: err instanceof Error ? err.message : 'An error occurred while creating the project'
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, defaultType, validateForm, onJobCreated, onClose, addressGeoData.latitude, addressGeoData.longitude, addressGeoData.mapbox_data]);

  const handleInputChange = useCallback((field: keyof FormData, value: string) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'client' && value !== prev.client) {
        updated.primary_contact = '';
        setIsAddingContact(false);
        setNewContactFirstName('');
        setNewContactLastName('');
      }
      return updated;
    });
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }

    if (field === 'job_number') {
      setIsDuplicate(false);
      if (duplicateCheckTimeoutRef.current) {
        clearTimeout(duplicateCheckTimeoutRef.current);
      }
      duplicateCheckTimeoutRef.current = setTimeout(() => {
        checkDuplicateJobNumber(value);
      }, 500);
    }
  }, [errors, checkDuplicateJobNumber]);

  const handleAddressSelected = useCallback((address: AddressFields) => {
    setFormData(prev => ({
      ...prev,
      street_address: address.street_address,
      city: address.city,
      state: address.state,
      postcode: address.postcode,
      country: address.country,
    }));
    setSiteAddressDisplay(
      address.mapbox_data?.full_address ||
      [
        address.street_address,
        [address.city, address.state, address.postcode].filter(Boolean).join(' '),
        address.country && address.country !== 'Australia' ? address.country : null,
      ].filter(Boolean).join(', ')
    );
    setAddressGeoData({
      latitude: address.latitude,
      longitude: address.longitude,
      mapbox_data: address.mapbox_data as Record<string, string> | null,
    });
  }, []);

  // Handle creating a new client
  const handleCreateClient = useCallback(async () => {
    if (!newClientName.trim()) {
      setErrors(prev => ({ ...prev, newClient: 'Client name is required' }));
      return;
    }

    setIsCreatingClient(true);
    setErrors(prev => ({ ...prev, newClient: undefined }));

    try {
      const { data, error } = await supabase
        .from('clients')
        .insert({ company_name: newClientName.trim() })
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Set the newly created client as selected
      handleInputChange('client', data.id.toString());
      
      // Force the SearchableClientDropdown to refresh by changing its key
      setRefreshKey(prev => prev + 1);
      
      // Reset add client state
      setIsAddingClient(false);
      setNewClientName('');
      
    } catch (err) {
      console.error('Error creating client:', err);
      setErrors(prev => ({ 
        ...prev, 
        newClient: err instanceof Error ? err.message : 'Failed to create client' 
      }));
    } finally {
      setIsCreatingClient(false);
    }
  }, [newClientName, handleInputChange]);

  const handleCancelAddClient = useCallback(() => {
    setIsAddingClient(false);
    setNewClientName('');
    setErrors(prev => ({ ...prev, newClient: undefined }));
  }, []);

  const handleCreateContact = useCallback(async () => {
    if (!newContactFirstName.trim()) {
      setErrors(prev => ({ ...prev, newContact: 'First name is required' }));
      return;
    }
    if (!newContactLastName.trim()) {
      setErrors(prev => ({ ...prev, newContact: 'Last name is required' }));
      return;
    }

    setIsCreatingContact(true);
    setErrors(prev => ({ ...prev, newContact: undefined }));

    try {
      const { data, error } = await supabase
        .from('contacts')
        .insert({
          first_name: newContactFirstName.trim(),
          last_name: newContactLastName.trim(),
          client_id: parseInt(formData.client, 10)
        })
        .select()
        .single();

      if (error) throw error;

      handleInputChange('primary_contact', data.id.toString());
      setContactRefreshKey(prev => prev + 1);
      setIsAddingContact(false);
      setNewContactFirstName('');
      setNewContactLastName('');
    } catch (err) {
      console.error('Error creating contact:', err);
      setErrors(prev => ({
        ...prev,
        newContact: err instanceof Error ? err.message : 'Failed to create contact'
      }));
    } finally {
      setIsCreatingContact(false);
    }
  }, [newContactFirstName, newContactLastName, formData.client, handleInputChange]);

  const handleCancelAddContact = useCallback(() => {
    setIsAddingContact(false);
    setNewContactFirstName('');
    setNewContactLastName('');
    setErrors(prev => ({ ...prev, newContact: undefined }));
  }, []);

  // Toggle team member selection
  const handleToggleTeamMember = useCallback((memberId: string) => {
    setFormData(prev => {
      const isSelected = prev.team_members.includes(memberId);
      if (isSelected) {
        // Remove member
        return {
          ...prev,
          team_members: prev.team_members.filter(id => id !== memberId)
        };
      } else {
        // Add member
        return {
          ...prev,
          team_members: [...prev.team_members, memberId]
        };
      }
    });
  }, []);

  const projectLeadDropdownItems: DropdownItem[] = [
    { id: '', label: 'None', value: '' },
    ...teamMembers.map(member => ({
      id: member.id.toString(),
      label: `${member.first_name} ${member.last_name}`,
      value: member.id.toString()
    }))
  ];

  const jobTypeOptions = PROJECT_JOB_TYPE_OPTIONS.map(type => ({
    value: type,
    label: type,
    hint: JOB_TYPE_HINTS[type],
  }));

  // Where the job number stands: being checked, taken, or free. The suggested
  // number is free by construction, so it is not announced as "available".
  const jobNumberTrimmed = formData.job_number.trim();
  const isSuggested = !!defaultJobNumber && jobNumberTrimmed === defaultJobNumber;
  const jobNumberState: SaveState = isCheckingDuplicate
    ? 'saving'
    : isDuplicate
    ? 'error'
    : jobNumberTrimmed && !isSuggested && !errors.job_number
    ? 'saved'
    : 'idle';
  const jobNumberHint = isCheckingDuplicate
    ? 'Checking…'
    : isSuggested
    ? 'Next available number'
    : jobNumberState === 'saved'
    ? 'Available'
    : undefined;

  // Dynamic title based on whether type is preset
  const modalTitle = defaultType ? `New ${defaultType} job` : 'New job';

  const modalFooter = (
    <div className="pill-row">
      <button type="button" onClick={onClose} className="pill-btn pill-btn--ghost">
        Cancel
      </button>
      <button
        type="submit"
        form="add-job-form"
        disabled={isSubmitting || isCheckingDuplicate || isDuplicate}
        className="pill-btn pill-btn--primary"
      >
        {isSubmitting ? 'Creating…' : 'Create job'}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      size="sm"
      hasFooter={true}
      footerContent={modalFooter}
    >
      <div className="form-content add-job-modal form-ui">
        <form id="add-job-form" onSubmit={handleSubmit}>
          {errors.general && (
            <p className="field__error" role="alert">{errors.general}</p>
          )}

          <PanelHeading title="Job" />
          <div className="pdm-grid pdm-grid--3">
            <Field label="Job number" required htmlFor="job_number" error={errors.job_number} hint={jobNumberHint}>
              <TextInput
                id="job_number"
                mono
                inputMode="numeric"
                value={formData.job_number}
                onChange={(e) => handleInputChange('job_number', e.target.value)}
                invalid={!!errors.job_number || isDuplicate}
                placeholder="Job number"
                trailing={<SaveDot state={jobNumberState} />}
              />
            </Field>
            <Field label="Description" required htmlFor="description" className="span-2" error={errors.description}>
              <TextInput
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                invalid={!!errors.description}
                placeholder="What the job is"
              />
            </Field>
            {/* Only offer the type when the caller has not fixed it */}
            {!defaultType && (
              <Field label="Type" required className="span-all" error={errors.types}>
                <SegmentedControl<ProjectJobType>
                  aria-label="Job type"
                  fullWidth
                  showHint
                  options={jobTypeOptions}
                  value={(formData.types as ProjectJobType) || null}
                  onChange={(value) => handleInputChange('types', value ?? '')}
                />
              </Field>
            )}
          </div>

          <PanelHeading spaced title="Client" />
          <div className="pdm-grid pdm-grid--2">
            <Field
              label="Client"
              action={!isAddingClient ? (
                <button type="button" className="panel-heading__action" onClick={() => setIsAddingClient(true)}>
                  + New client
                </button>
              ) : undefined}
            >
              <SearchableClientDropdown
                key={refreshKey}
                value={formData.client || null}
                onChange={(clientId) => {
                  handleInputChange('client', clientId ? clientId.toString() : '');
                }}
                placeholder="Search clients…"
                allowNone={true}
                noneLabel="No client"
                disabled={isAddingClient}
              />
              {isAddingClient && (
                <div className="disclosure-card">
                  <TextInput
                    value={newClientName}
                    onChange={(e) => {
                      setNewClientName(e.target.value);
                      if (errors.newClient) {
                        setErrors(prev => ({ ...prev, newClient: undefined }));
                      }
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateClient(); } }}
                    placeholder="Client name"
                    aria-label="New client name"
                    invalid={!!errors.newClient}
                    disabled={isCreatingClient}
                    autoFocus
                  />
                  {errors.newClient && <p className="field__error" role="alert">{errors.newClient}</p>}
                  <div className="disclosure-card__actions">
                    <button type="button" className="pill-btn pill-btn--ghost pill-btn--sm" onClick={handleCancelAddClient} disabled={isCreatingClient}>
                      Cancel
                    </button>
                    <button type="button" className="pill-btn pill-btn--primary pill-btn--sm" onClick={handleCreateClient} disabled={isCreatingClient || !newClientName.trim()}>
                      {isCreatingClient ? 'Creating…' : 'Create client'}
                    </button>
                  </div>
                </div>
              )}
            </Field>

            <Field
              label="Primary contact"
              action={formData.client && !isAddingContact ? (
                <button type="button" className="panel-heading__action" onClick={() => setIsAddingContact(true)}>
                  + New contact
                </button>
              ) : undefined}
            >
              <SearchableContactDropdown
                key={contactRefreshKey}
                value={formData.primary_contact || null}
                onChange={(contactId) => {
                  handleInputChange('primary_contact', contactId ? contactId.toString() : '');
                }}
                placeholder={formData.client ? 'Search contacts…' : 'Select a client first'}
                allowNone={true}
                noneLabel="No contact"
                clientId={formData.client ? parseInt(formData.client, 10) : undefined}
                disabled={!formData.client || isAddingContact}
              />
              {isAddingContact && (
                <div className="disclosure-card">
                  <div className="disclosure-card__row">
                    <TextInput
                      value={newContactFirstName}
                      onChange={(e) => {
                        setNewContactFirstName(e.target.value);
                        if (errors.newContact) {
                          setErrors(prev => ({ ...prev, newContact: undefined }));
                        }
                      }}
                      placeholder="First name"
                      aria-label="First name"
                      invalid={!!errors.newContact}
                      disabled={isCreatingContact}
                      autoFocus
                    />
                    <TextInput
                      value={newContactLastName}
                      onChange={(e) => {
                        setNewContactLastName(e.target.value);
                        if (errors.newContact) {
                          setErrors(prev => ({ ...prev, newContact: undefined }));
                        }
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateContact(); } }}
                      placeholder="Last name"
                      aria-label="Last name"
                      invalid={!!errors.newContact}
                      disabled={isCreatingContact}
                    />
                  </div>
                  {errors.newContact && <p className="field__error" role="alert">{errors.newContact}</p>}
                  <div className="disclosure-card__actions">
                    <button type="button" className="pill-btn pill-btn--ghost pill-btn--sm" onClick={handleCancelAddContact} disabled={isCreatingContact}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="pill-btn pill-btn--primary pill-btn--sm"
                      onClick={handleCreateContact}
                      disabled={isCreatingContact || !newContactFirstName.trim() || !newContactLastName.trim()}
                    >
                      {isCreatingContact ? 'Creating…' : 'Create contact'}
                    </button>
                  </div>
                </div>
              )}
            </Field>
          </div>

          <PanelHeading spaced title="Site" />
          <div className="pdm-grid pdm-grid--1">
            <Field
              label="Site address"
              action={
                <IconButton
                  size="sm"
                  icon={<ChevronDownIcon />}
                  label={showAddressDetails ? 'Hide address fields' : 'Show address fields'}
                  active={showAddressDetails}
                  onClick={() => setShowAddressDetails(prev => !prev)}
                />
              }
            >
              <AddressAutocomplete
                onPlaceSelected={handleAddressSelected}
                value={siteAddressDisplay}
                onChange={(val) => setSiteAddressDisplay(val)}
                placeholder="Search address…"
              />
            </Field>
          </div>
          {showAddressDetails && (
            <div className="pdm-grid pdm-grid--2">
              <Field label="Street" htmlFor="street_address" className="span-all">
                <TextInput id="street_address" value={formData.street_address} onChange={(e) => handleInputChange('street_address', e.target.value)} placeholder="Street address" />
              </Field>
              <Field label="City / suburb" htmlFor="city">
                <TextInput id="city" value={formData.city} onChange={(e) => handleInputChange('city', e.target.value)} placeholder="City" />
              </Field>
              <Field label="State" htmlFor="state">
                <TextInput id="state" value={formData.state} onChange={(e) => handleInputChange('state', e.target.value)} placeholder="State" />
              </Field>
              <Field label="Postcode" htmlFor="postcode">
                <TextInput id="postcode" mono value={formData.postcode} onChange={(e) => handleInputChange('postcode', e.target.value)} placeholder="Postcode" />
              </Field>
              <Field label="Country" htmlFor="country">
                <TextInput id="country" value={formData.country} onChange={(e) => handleInputChange('country', e.target.value)} placeholder="Country" />
              </Field>
            </div>
          )}

          <PanelHeading spaced title="Team" />
          <div className="pdm-grid pdm-grid--1">
            <Field label="Project lead" hint="The lead is always on the team.">
              <Dropdown
                items={projectLeadDropdownItems}
                value={formData.project_lead}
                onChange={(value) => handleInputChange('project_lead', value)}
                placeholder="Select project lead…"
                searchable
              />
            </Field>
            <Field
              label="Team members"
              hint={formData.team_members.length > 0
                ? `${formData.team_members.length} ${formData.team_members.length === 1 ? 'member' : 'members'} selected`
                : 'Nobody selected yet'}
            >
              <MemberChips
                members={teamMembers}
                selected={formData.team_members}
                leadId={formData.project_lead || undefined}
                onToggle={handleToggleTeamMember}
              />
            </Field>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default AddJobModal;
